/**
 * Employee Home — the focused landing page for non-admin users.
 *
 * Shows everything an employee needs in one place:
 *  - Their personal card
 *  - Their supervisor card (with quick contact)
 *  - Credentials at-a-glance (expiring badges + healthy count)
 *  - PTO balance + quick request link
 *  - Upcoming events (interviews, meetings, signings)
 *  - Pending signature requests
 *  - Recent visits
 *
 * Works in three modes:
 *  - Real non-admin login
 *  - Admin "View As" of a real user
 *  - Admin "View As" of a personnel record without an account (simulated)
 *
 * All data comes from a single aggregating endpoint: GET /api/me/dashboard
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  User as UserIcon, Briefcase, Phone, Mail, Calendar, FileSignature,
  ShieldAlert, ShieldCheck, AlertTriangle, Clock, MapPin, ListChecks,
  ChevronRight, UserCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TONE = {
  ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  expiring_soon: { label: 'Expiring Soon', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  expired: { label: 'Expired', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  no_expiry: { label: 'No expiry', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

const SectionCard = ({ icon: Icon, title, action, children, testId }) => (
  <Card className="overflow-hidden" data-testid={testId}>
    <CardHeader className="pb-3 flex flex-row items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon ? <Icon size={18} className="text-primary" /> : null}
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </div>
      {action}
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
);

export default function EmployeeHome() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/me/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(resp.data);
      } catch (e) {
        toast.error("Couldn't load your dashboard.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 p-4 md:p-6 max-w-6xl mx-auto" data-testid="employee-home-loading">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!data?.person) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="employee-home-empty">
        <UserIcon size={48} className="text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">No personnel record linked</h2>
        <p className="text-sm text-muted-foreground">
          Your account isn&apos;t linked to a personnel record yet. Please contact your administrator.
        </p>
      </div>
    );
  }

  const { person, supervisor, credentials, pto, events, signature_requests_pending: sigs, recent_visits: visits } = data;
  const displayName = person.preferred_name?.trim() || person.first_name;
  const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();

  return (
    <div className="space-y-5 p-4 md:p-6 max-w-6xl mx-auto" data-testid="employee-home">
      {/* Greeting / hero band */}
      <div
        className="rounded-xl bg-gradient-to-r from-slate-900 to-slate-700 text-white p-5 md:p-7 shadow-sm"
        data-testid="employee-home-greeting"
      >
        <p className="text-xs uppercase tracking-wider text-slate-300 font-medium">Welcome back</p>
        <h1 className="text-2xl md:text-3xl font-bold mt-1">{displayName}</h1>
        <p className="text-slate-300 text-sm mt-1">
          {[person.job_title, person.discipline, person.department].filter(Boolean).join(' · ') || 'Employee'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Personal card */}
        <SectionCard icon={UserIcon} title="My Profile" testId="card-profile">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Full Name</span><span className="font-medium">{fullName || '—'}</span></div>
            {person.employee_id && (
              <div className="flex justify-between"><span className="text-muted-foreground">Employee ID</span><span className="font-medium">{person.employee_id}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Hire Date</span><span className="font-medium">{fmtDate(person.hire_date)}</span></div>
            {person.work_email && (
              <div className="flex justify-between gap-2 items-center">
                <span className="text-muted-foreground">Work Email</span>
                <a href={`mailto:${person.work_email}`} className="font-medium text-primary truncate">{person.work_email}</a>
              </div>
            )}
            {person.mobile_phone && (
              <div className="flex justify-between"><span className="text-muted-foreground">Mobile</span><span className="font-medium">{person.mobile_phone}</span></div>
            )}
          </div>
          <div className="mt-4 pt-3 border-t flex gap-2">
            <Link to="/my-profile" className="text-xs text-primary font-medium hover:underline">View full profile →</Link>
          </div>
        </SectionCard>

        {/* Supervisor card */}
        <SectionCard icon={UserCheck} title="My Supervisor" testId="card-supervisor">
          {supervisor ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  {(supervisor.first_name?.[0] || '?')}{(supervisor.last_name?.[0] || '')}
                </div>
                <div>
                  <p className="font-semibold">{supervisor.first_name} {supervisor.last_name}</p>
                  {supervisor.job_title && (
                    <p className="text-xs text-muted-foreground">{supervisor.job_title}</p>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 pt-2">
                {(supervisor.work_email || supervisor.email) && (
                  <a href={`mailto:${supervisor.work_email || supervisor.email}`} className="flex items-center gap-2 text-primary hover:underline">
                    <Mail size={14} />{supervisor.work_email || supervisor.email}
                  </a>
                )}
                {(supervisor.mobile_phone || supervisor.phone) && (
                  <a href={`tel:${supervisor.mobile_phone || supervisor.phone}`} className="flex items-center gap-2 text-primary hover:underline">
                    <Phone size={14} />{supervisor.mobile_phone || supervisor.phone}
                  </a>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No supervisor assigned. Ask an admin to set one on your profile.</p>
          )}
        </SectionCard>

        {/* Credentials at-a-glance */}
        <SectionCard
          icon={ShieldCheck}
          title="My Credentials"
          action={<Link to="/my-profile" className="text-xs text-primary hover:underline">Manage →</Link>}
          testId="card-credentials"
        >
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 bg-emerald-50 rounded">
              <p className="text-xl font-bold text-emerald-700">{credentials?.stats?.ok || 0}</p>
              <p className="text-[10px] uppercase text-emerald-700 tracking-wide">Valid</p>
            </div>
            <div className="text-center p-2 bg-amber-50 rounded">
              <p className="text-xl font-bold text-amber-700">{credentials?.stats?.expiring_soon || 0}</p>
              <p className="text-[10px] uppercase text-amber-700 tracking-wide">Expiring</p>
            </div>
            <div className="text-center p-2 bg-rose-50 rounded">
              <p className="text-xl font-bold text-rose-700">{credentials?.stats?.expired || 0}</p>
              <p className="text-[10px] uppercase text-rose-700 tracking-wide">Expired</p>
            </div>
          </div>
          {(credentials?.items || []).filter(c => c.status !== 'ok' && c.status !== 'no_expiry').slice(0, 5).map(c => (
            <div key={c.id} className="flex items-center justify-between text-xs py-1.5 border-t">
              <span className="font-medium truncate">{c.document_type || c.file_name || 'Credential'}</span>
              <Badge className={`ml-2 text-[10px] ${STATUS_TONE[c.status]?.cls || ''}`} variant="outline">
                {fmtDate(c.expiration_date)}
              </Badge>
            </div>
          ))}
          {(credentials?.items || []).filter(c => c.status !== 'ok' && c.status !== 'no_expiry').length === 0 && (
            <p className="text-xs text-muted-foreground italic mt-2">All credentials are up to date.</p>
          )}
        </SectionCard>

        {/* PTO */}
        <SectionCard icon={Clock} title="My Time Off" testId="card-pto">
          {pto ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Vacation</p>
                  <p className="text-2xl font-bold mt-0.5">{pto.vacation_available}</p>
                  <p className="text-[10px] text-muted-foreground">days available</p>
                </div>
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Sick</p>
                  <p className="text-2xl font-bold mt-0.5">{pto.sick_available}</p>
                  <p className="text-[10px] text-muted-foreground">days available</p>
                </div>
              </div>
              {pto.pending_requests?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium text-amber-600">{pto.pending_requests.length} pending request{pto.pending_requests.length === 1 ? '' : 's'}</p>
                </div>
              )}
              <Link to="/my-profile" className="text-xs text-primary font-medium hover:underline block">Request time off →</Link>
            </div>
          ) : <p className="text-sm text-muted-foreground italic">No PTO balance set.</p>}
        </SectionCard>

        {/* Upcoming events */}
        <SectionCard icon={Calendar} title="Upcoming" testId="card-events">
          {events?.length > 0 ? (
            <ul className="space-y-2.5">
              {events.slice(0, 6).map(ev => (
                <li key={ev.id} className="flex items-start gap-3 text-sm">
                  <div className="mt-0.5 w-1 h-10 rounded-full bg-primary/60" />
                  <div className="flex-1">
                    <p className="font-medium truncate">{ev.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtDateTime(ev.start_at)} {ev.location ? `· ${ev.location}` : ''}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{ev.type || 'Event'}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground italic">Nothing on the calendar yet.</p>
          )}
        </SectionCard>

        {/* Signature requests */}
        <SectionCard icon={FileSignature} title="Documents to Sign" testId="card-signatures">
          {sigs?.length > 0 ? (
            <ul className="space-y-2">
              {sigs.map(s => (
                <li key={s.id} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{s.template_name || s.document_name || 'Document'}</p>
                    <p className="text-[10px] text-amber-700 uppercase tracking-wide">Action required</p>
                  </div>
                  <ChevronRight size={16} className="text-amber-600 flex-shrink-0" />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground italic">No signature requests pending.</p>
          )}
        </SectionCard>

        {/* Recent visits */}
        <SectionCard icon={MapPin} title="Recent Visits" testId="card-visits">
          {visits?.length > 0 ? (
            <ul className="space-y-1.5 text-xs">
              {visits.slice(0, 7).map((v, i) => (
                <li key={i} className="flex justify-between py-1 border-t first:border-t-0">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{v.task_name || '—'}</span>
                    {v.city && <span className="text-muted-foreground ml-2">{v.city}</span>}
                  </div>
                  <span className="text-muted-foreground ml-2 flex-shrink-0">{v.date}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground italic">No recent visits.</p>
          )}
        </SectionCard>

        {/* Quick Links */}
        <SectionCard icon={ListChecks} title="Quick Links" testId="card-quick-links">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Link to="/my-profile" className="p-2 border rounded hover:bg-accent transition text-center" data-testid="quick-link-profile">My Profile</Link>
            <Link to="/my-profile?tab=time-off" className="p-2 border rounded hover:bg-accent transition text-center" data-testid="quick-link-pto">Request PTO</Link>
            <Link to="/my-profile" className="p-2 border rounded hover:bg-accent transition text-center" data-testid="quick-link-docs">My Documents</Link>
            <Link to="/notifications" className="p-2 border rounded hover:bg-accent transition text-center" data-testid="quick-link-notifs">Notifications</Link>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
